:root {
  --bg: #f6f6f6;
  --surface: #ffffff;
  --text: #333333;
  --muted: #6c757d;
  --accent: #f7a600;
  --accent-hover: #e09600;
  --accent-soft: rgba(247, 166, 0, 0.10);
  --accent-ring: rgba(247, 166, 0, 0.22);
  --danger: #d70015;
  --border: rgba(0, 0, 0, 0.08);
  --shadow-1: 0 1px 3px rgba(0, 0, 0, 0.04);
  --shadow-2: 0 8px 24px rgba(0, 0, 0, 0.06);
  --nav-height: 64px;
  --radius: 12px;
  --radius-sm: 8px;
}

* { box-sizing: border-box; }

html, body, #app {
  height: 100%;
  margin: 0;
  padding: 0;
}

body {
  background: var(--bg);
  color: var(--text);
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro Display",
               "Segoe UI", "Inter", "Helvetica Neue", Arial, sans-serif;
  font-size: 16px;
  line-height: 1.5;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
}

.card {
  background: var(--surface);
  border-radius: var(--radius);
  box-shadow: var(--shadow-1), var(--shadow-2);
  padding: 48px;
  width: 100%;
  max-width: 480px;
}

.title {
  font-size: 32px;
  font-weight: 600;
  letter-spacing: -0.02em;
  margin: 0;
}

.subtitle {
  color: var(--muted);
  margin-top: 8px;
  font-size: 15px;
}

.kicker {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--muted);
  margin-bottom: 16px;
}

.card-centered {
  text-align: center;
}

/* Login-/Apple-Pill-Button — exklusiv für die Home-View (Anmelden).
   Nicht auf alle .btn anwenden, damit Bootstrap-Buttons in Dashboard/
   Servicepartner mit ihren nativen Form-Faktoren erhalten bleiben. */
.btn-login {
  display: block;
  min-width: 240px;
  margin: 32px auto 0;
  padding: 12px 20px;
  border: none;
  border-radius: 9999px;
  background: var(--accent);
  color: #fff;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 120ms ease, transform 80ms ease;
}

.btn-login:hover { background: var(--accent-hover); }
.btn-login:active { transform: scale(0.98); }
.btn-login:focus-visible {
  outline: none;
  box-shadow: 0 0 0 4px var(--accent-ring);
}

.linkbtn {
  margin-top: 24px;
  display: inline-block;
  background: none;
  border: none;
  color: var(--accent);
  font: inherit;
  font-size: 14px;
  cursor: pointer;
  padding: 0;
}

.linkbtn:hover { text-decoration: underline; }

.error {
  margin-top: 24px;
  padding: 12px 16px;
  background: rgba(215, 0, 21, 0.08);
  border: 1px solid rgba(215, 0, 21, 0.16);
  border-radius: var(--radius-sm);
  color: var(--danger);
  font-size: 14px;
}

.meta {
  margin-top: 32px;
  font-size: 12px;
  color: var(--muted);
}

.spinner {
  margin: 16px 0;
  width: 24px;
  height: 24px;
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-top-color: var(--accent);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* --- Top-Navigation ------------------------------------------------------ */

.topnav {
  position: sticky;
  top: 0;
  z-index: 10;
  background: var(--surface);
  border-bottom: 1px solid var(--border);
  box-shadow: var(--shadow-1);
}

.topnav-inner {
  max-width: 1200px;
  margin: 0 auto;
  height: var(--nav-height);
  padding: 0 24px;
  display: flex;
  align-items: center;
  gap: 24px;
}

/* Wrapper um Menü + User + Logout. Auf dem Desktop "transparent" (display:
   contents), damit die Kinder direkte Flex-Items von .topnav-inner bleiben und
   das bisherige Layout exakt erhalten bleibt. Auf Mobil wird daraus ein
   ausklappbares Panel (siehe Media-Query unten). */
.topnav-collapse {
  display: contents;
}

/* Hamburger-Button — nur auf Mobil sichtbar. */
.topnav-toggle {
  display: none;
  flex-direction: column;
  justify-content: center;
  gap: 5px;
  width: 40px;
  height: 40px;
  margin-left: auto;
  padding: 0;
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  background: var(--surface);
  cursor: pointer;
}

.topnav-toggle-bar {
  display: block;
  width: 20px;
  height: 2px;
  margin: 0 auto;
  background: var(--text);
  border-radius: 2px;
  transition: transform 160ms ease, opacity 160ms ease;
}

.topnav-toggle.is-open .topnav-toggle-bar:nth-child(1) {
  transform: translateY(7px) rotate(45deg);
}
.topnav-toggle.is-open .topnav-toggle-bar:nth-child(2) {
  opacity: 0;
}
.topnav-toggle.is-open .topnav-toggle-bar:nth-child(3) {
  transform: translateY(-7px) rotate(-45deg);
}

.topnav-brand {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-weight: 700;
  font-size: 18px;
  letter-spacing: -0.01em;
  color: var(--text);
  text-decoration: none;
}

.topnav-brand-mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: var(--accent);
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.topnav-menu {
  display: flex;
  align-items: center;
  gap: 4px;
  list-style: none;
  margin: 0;
  padding: 0;
}

.topnav-menu a,
.topnav-menu button {
  display: inline-flex;
  align-items: center;
  padding: 8px 14px;
  border: none;
  background: transparent;
  color: var(--text);
  font: inherit;
  font-size: 14px;
  font-weight: 500;
  border-radius: 8px;
  cursor: pointer;
  text-decoration: none;
  transition: background-color 120ms ease, color 120ms ease;
}

.topnav-menu a:hover,
.topnav-menu button:hover {
  background: var(--accent-soft);
  color: var(--accent-hover);
}

.topnav-menu a.is-active {
  background: var(--accent-soft);
  color: var(--accent-hover);
}

/* --- Topnav-Submenu (Hover/Fokus, Tastatur-A11y) ------------------------- */

.topnav-menu li {
  position: relative;
}

.topnav-submenu {
  position: absolute;
  top: 100%;
  left: 0;
  min-width: 220px;
  margin: 4px 0 0;
  padding: 6px;
  list-style: none;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-2);
  opacity: 0;
  visibility: hidden;
  transform: translateY(-4px);
  transition: opacity 120ms ease, transform 120ms ease, visibility 0s 120ms;
  z-index: 20;
}

.topnav-menu li.has-submenu:hover > .topnav-submenu,
.topnav-menu li.has-submenu:focus-within > .topnav-submenu {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
  transition: opacity 120ms ease, transform 120ms ease, visibility 0s;
}

.topnav-submenu a {
  display: block;
  padding: 8px 12px;
  border-radius: 6px;
  color: var(--text);
  font-size: 14px;
  text-decoration: none;
}

.topnav-submenu a:hover,
.topnav-submenu a:focus-visible {
  background: var(--accent-soft);
  color: var(--accent-hover);
  outline: none;
}

.topnav-spacer {
  flex: 1;
}

.topnav-user {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 6px 10px 6px 6px;
  border-radius: 9999px;
  background: var(--bg);
  color: var(--text);
  font-size: 14px;
  font-weight: 500;
}

.topnav-user-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--accent);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.topnav-user-name {
  white-space: nowrap;
}

.topnav-logout {
  border: none;
  background: transparent;
  color: var(--muted);
  font: inherit;
  font-size: 14px;
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 120ms ease, color 120ms ease;
}

.topnav-logout:hover {
  background: var(--bg);
  color: var(--text);
}

/* --- Dashboard ----------------------------------------------------------- */

.dashboard {
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 24px 64px;
}

.dashboard-header {
  margin-bottom: 32px;
}

.dashboard-title {
  font-size: 28px;
  font-weight: 600;
  letter-spacing: -0.02em;
  margin: 0;
}

.dashboard-subtitle {
  color: var(--muted);
  margin-top: 4px;
  font-size: 15px;
}

.dashboard-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 16px;
}

.tile {
  background: var(--surface);
  border-radius: var(--radius);
  padding: 20px;
  box-shadow: var(--shadow-1), var(--shadow-2);
}

.tile-label {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--muted);
}

.tile-value {
  margin-top: 8px;
  font-size: 24px;
  font-weight: 600;
  letter-spacing: -0.01em;
}

/* --- Stammdaten-/Liste-Seiten -------------------------------------------- */

.dashboard-header-row {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
}

.empty-state {
  background: var(--surface);
  border: 1px dashed var(--border);
  border-radius: var(--radius);
  padding: 32px;
  text-align: center;
  color: var(--muted);
}

/* Bootstrap-Brand-Override: btn-primary nutzt die Handyshop-Akzentfarbe
   (#f7a600) statt Bootstrap-Blau. Nur Hintergrund/Border, Bootstrap-Layout
   (Padding, Border-Radius, Disabled-State) bleibt unangetastet. */
.btn-primary,
.btn-primary:disabled,
.btn-primary.disabled {
  background-color: var(--accent);
  border-color:     var(--accent);
  color: #fff;
}
.btn-primary:hover {
  background-color: var(--accent-hover);
  border-color:     var(--accent-hover);
  color: #fff;
}
.btn-primary:focus,
.btn-primary:focus-visible {
  background-color: var(--accent-hover);
  border-color:     var(--accent-hover);
  color: #fff;
  box-shadow: 0 0 0 0.25rem var(--accent-ring);
}
.btn-primary:active,
.btn-primary.active,
.btn-primary:not(:disabled):not(.disabled):active {
  background-color: var(--accent-hover);
  border-color:     var(--accent-hover);
  color: #fff;
}

/* --- Ticket-/Detail-Sektionen (zentral, ein Kartenstil für alle Panels) --- */

/* Eine Karte = weiche Elevation statt Vollrahmen (weniger "umrandete Box").
   Wird auf die <section class="ticket-section"> in TicketDetail und den
   eingebetteten Panels (Dateien, Kostenvoranschläge) angewendet. */
.ticket-section {
  margin-top: 16px;
  padding: 20px 24px;
  background: var(--surface);
  border-radius: var(--radius);
  box-shadow: var(--shadow-1), var(--shadow-2);
}

.ticket-section-title {
  margin: 0 0 16px;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: -0.01em;
}

/* --- Sekundäre Buttons an die Token-Palette angleichen (statt rohem
   Bootstrap-Grau/-Gelb/-Rot, die neben dem Apple-Look "fremd" wirken). --- */
.btn-outline-secondary {
  color: var(--text);
  border-color: var(--border);
  background: var(--surface);
}
.btn-outline-secondary:hover {
  color: var(--text);
  background: var(--bg);
  border-color: rgba(0, 0, 0, 0.16);
}
.btn-outline-secondary:focus,
.btn-outline-secondary:focus-visible {
  box-shadow: 0 0 0 0.25rem var(--accent-ring);
}

/* --- Responsive ---------------------------------------------------------- */

/* Mittlere Viewports: etwas weniger Außenabstand. */
@media (max-width: 992px) {
  .dashboard {
    padding: 32px 16px 48px;
  }
}

/* Mobil / Tablet-Hochformat: Topnav klappt zum Hamburger-Menü auf. */
@media (max-width: 768px) {
  .topnav-inner {
    padding: 0 16px;
    gap: 12px;
  }

  .topnav-toggle {
    display: flex;
  }

  /* Aus dem "display: contents"-Wrapper wird ein ausklappbares Panel,
     das unter der Topnav-Leiste über die volle Breite aufgeht. */
  .topnav-collapse {
    display: none;
    position: absolute;
    top: var(--nav-height);
    left: 0;
    right: 0;
    flex-direction: column;
    align-items: stretch;
    gap: 4px;
    padding: 12px 16px 16px;
    background: var(--surface);
    border-bottom: 1px solid var(--border);
    box-shadow: var(--shadow-2);
  }

  .topnav-collapse.is-open {
    display: flex;
  }

  .topnav-menu {
    flex-direction: column;
    align-items: stretch;
    gap: 2px;
    width: 100%;
  }

  .topnav-menu a,
  .topnav-menu button {
    width: 100%;
    padding: 12px 14px;
    font-size: 16px;
  }

  /* Submenü auf Mobil immer aufgeklappt darstellen (kein Hover nötig). */
  .topnav-submenu {
    position: static;
    opacity: 1;
    visibility: visible;
    transform: none;
    min-width: 0;
    margin: 0 0 4px;
    padding: 0 0 4px 12px;
    border: none;
    box-shadow: none;
    transition: none;
  }

  .topnav-submenu a {
    padding: 10px 14px;
  }

  .topnav-spacer {
    display: none;
  }

  .topnav-user {
    margin-top: 8px;
    align-self: flex-start;
  }

  .topnav-logout {
    align-self: flex-start;
    padding: 12px 14px;
    font-size: 16px;
  }

  /* Listen-Header (Titel + Aktionsbutton) untereinander statt nebeneinander. */
  .dashboard-header-row {
    flex-direction: column;
    align-items: stretch;
  }

  .dashboard-title {
    font-size: 24px;
  }

  .page {
    padding: 24px 16px;
  }

  .card {
    padding: 28px 20px;
  }

  .title {
    font-size: 26px;
  }
}
